import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import ExperimentOutlined from '@ant-design/icons/ExperimentOutlined';
import ReloadOutlined from '@ant-design/icons/ReloadOutlined';
import Alert from 'antd/es/alert';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Input from 'antd/es/input';
import Space from 'antd/es/space';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import styled from 'styled-components';

import type { JSONSchemaType } from '@mai/types';

const { Text } = Typography;
const { TextArea } = Input;

const StyledCard = styled(Card)`
  ${({ theme }) => `
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .ant-card-body {
    flex: 1;
    padding: ${theme.padding}px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
`}
`;

const ValidationContent = styled.div`
  ${({ theme }) => `
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.marginMD}px;
  overflow: hidden;
`}
`;

const TestDataInput = styled(TextArea)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  font-family: ${theme.fontFamilyCode};
`}
`;

const SectionLabel = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  display: block;
  margin-bottom: ${theme.marginXS}px;
`}
`;

const ErrorList = styled.ul`
  ${({ theme }) => `
  margin: 0;
  padding-left: ${theme.paddingMD}px;
`}
`;

const ErrorText = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
`}
`;

const ValidationSpace = styled(Space)`
  ${({ theme }) => `
  margin-bottom: ${theme.marginXS}px;
`}
`;

const ResultContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

interface SchemaValidationProps {
  schema: JSONSchemaType;
}

export const SchemaValidation: React.FC<SchemaValidationProps> = ({
  schema,
}) => {
  const [testData, setTestData] = useState('');
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    errors: string[];
  } | null>(null);

  const generateSampleData = (schema: JSONSchemaType): unknown => {
    if (!schema || !schema.type) return null;

    switch (schema.type) {
      case 'string':
        if (schema.format === 'email') return 'user@example.com';
        if (schema.format === 'date') return '2024-01-01';
        if (schema.format === 'date-time') return '2024-01-01T12:00:00Z';
        if (schema.format === 'uri') return 'https://example.com';
        if (schema.format === 'uuid')
          return '550e8400-e29b-41d4-a716-446655440000';
        return 'example string';

      case 'number':
        return schema.minimum || 42.5;

      case 'integer':
        return schema.minimum || 42;

      case 'boolean':
        return true;

      case 'array':
        if (schema.items && !Array.isArray(schema.items)) {
          return [generateSampleData(schema.items)];
        }
        return [];

      case 'object':
        if (schema.properties) {
          const sample: Record<string, unknown> = {};
          Object.entries(schema.properties).forEach(([key, propSchema]) => {
            sample[key] = generateSampleData(propSchema);
          });
          return sample;
        }
        return {};

      default:
        return null;
    }
  };

  const handleGenerateSample = () => {
    const sampleData = generateSampleData(schema);
    setTestData(JSON.stringify(sampleData, null, 2));
    setValidationResult(null);
  };

  const validateData = () => {
    if (!testData.trim()) {
      setValidationResult({
        valid: false,
        errors: ['Please enter test data to validate'],
      });
      return;
    }

    try {
      const data = JSON.parse(testData);
      const errors: string[] = [];

      // Basic validation based on schema
      const validateValue = (
        value: unknown,
        schema: JSONSchemaType,
        path: string = 'root',
      ): void => {
        // Type validation
        if (schema.type) {
          const actualType = Array.isArray(value) ? 'array' : typeof value;
          if (
            actualType !== schema.type &&
            !(schema.type === 'integer' && Number.isInteger(value))
          ) {
            errors.push(
              `${path}: Expected type ${schema.type}, got ${actualType}`,
            );
            return;
          }
        }

        // String validations
        if (schema.type === 'string' && typeof value === 'string') {
          if (schema.minLength && value.length < schema.minLength) {
            errors.push(
              `${path}: String length ${value.length} is less than minimum ${schema.minLength}`,
            );
          }
          if (schema.maxLength && value.length > schema.maxLength) {
            errors.push(
              `${path}: String length ${value.length} exceeds maximum ${schema.maxLength}`,
            );
          }
          if (schema.pattern) {
            try {
              const regex = new RegExp(schema.pattern);
              if (!regex.test(value)) {
                errors.push(
                  `${path}: String does not match pattern ${schema.pattern}`,
                );
              }
            } catch (e) {
              // Invalid regex
            }
          }
          if (schema.format) {
            // Basic format validation
            if (schema.format === 'email' && !value.includes('@')) {
              errors.push(`${path}: Invalid email format`);
            }
            if (schema.format === 'uri' && !value.startsWith('http')) {
              errors.push(`${path}: Invalid URI format`);
            }
          }
        }

        // Number validations
        if (
          (schema.type === 'number' || schema.type === 'integer') &&
          typeof value === 'number'
        ) {
          if (schema.minimum !== undefined && value < schema.minimum) {
            errors.push(
              `${path}: Value ${value} is less than minimum ${schema.minimum}`,
            );
          }
          if (schema.maximum !== undefined && value > schema.maximum) {
            errors.push(
              `${path}: Value ${value} exceeds maximum ${schema.maximum}`,
            );
          }
        }

        // Object validations
        if (
          schema.type === 'object' &&
          typeof value === 'object' &&
          value !== null
        ) {
          const valueObj = value as Record<string, unknown>;
          // Check required properties
          if (schema.required && Array.isArray(schema.required)) {
            schema.required.forEach((prop: string) => {
              if (!(prop in valueObj)) {
                errors.push(`${path}: Missing required property '${prop}'`);
              }
            });
          }

          // Validate properties
          if (schema.properties) {
            Object.entries(valueObj).forEach(([key, val]) => {
              if (schema.properties?.[key]) {
                validateValue(val, schema.properties[key], `${path}.${key}`);
              } else if (schema.additionalProperties === false) {
                errors.push(
                  `${path}: Additional property '${key}' is not allowed`,
                );
              }
            });
          }
        }

        // Array validations
        if (schema.type === 'array' && Array.isArray(value)) {
          if (schema.items && !Array.isArray(schema.items)) {
            value.forEach((item, index) => {
              validateValue(
                item,
                schema.items as JSONSchemaType,
                `${path}[${index}]`,
              );
            });
          }
        }
      };

      validateValue(data, schema);

      setValidationResult({
        valid: errors.length === 0,
        errors,
      });
    } catch (e) {
      setValidationResult({
        valid: false,
        errors: [
          'Invalid JSON: ' + (e instanceof Error ? e.message : 'Unknown error'),
        ],
      });
    }
  };

  return (
    <StyledCard
      title="Test Your Schema"
      size="small"
      extra={
        <Space size="small">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={handleGenerateSample}
          >
            Generate Sample
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<ExperimentOutlined />}
            onClick={validateData}
          >
            Validate
          </Button>
        </Space>
      }
    >
      <ValidationContent>
        <div>
          <SectionLabel type="secondary">TEST DATA</SectionLabel>
          <TestDataInput
            rows={6}
            value={testData}
            onChange={(e) => setTestData(e.target.value)}
            placeholder="Enter JSON data to test against the schema..."
          />
        </div>

        <ResultContainer>
          {validationResult && (
            <Alert
              message={validationResult.valid ? 'Valid' : 'Invalid'}
              description={
                validationResult.valid ? (
                  <Space>
                    <CheckCircleOutlined />
                    The data matches your schema perfectly!
                  </Space>
                ) : (
                  <div>
                    <ValidationSpace>
                      <CloseCircleOutlined />
                      <Text strong>Validation failed</Text>
                    </ValidationSpace>
                    <ErrorList>
                      {validationResult.errors.map((error, index) => (
                        <li key={index}>
                          <ErrorText type="danger">{error}</ErrorText>
                        </li>
                      ))}
                    </ErrorList>
                  </div>
                )
              }
              type={validationResult.valid ? 'success' : 'error'}
              showIcon
            />
          )}
        </ResultContainer>
      </ValidationContent>
    </StyledCard>
  );
};
